/**
 * Hook: GET /api/t/{slug}/rankings?jd_id=<id> — A30b-2 rank-ordered list.
 *
 * Returns the candidate list from the JD's latest *complete* run.
 * Each item carries ``candidate_status`` so the Dashboard can apply the
 * dimmed treatment to terminal-state cards (opted_out + rejected) without
 * a second fetch.
 *
 * Cache key: ``['rankings', tenantSlug, jdId]``. 30c, 30d also consume.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import type { FitOverall } from "@/lib/fitScore";

export type TenureSignal = "strong" | "moderate" | "concern";

interface RankedCandidate {
  candidate_id: string;
  name: string;
  rank_position: number;
  summary: string;
  tenure_signal: TenureSignal;
  fit_overall: FitOverall;
  candidate_status: CandidateStatus;
  /**
   * Batch 30h Commit 1: AI-extracted skill tags (≤6 strings, ≤40 chars
   * each). Forward-only — legacy rankings rows return `[]`. Surfaced on
   * the dashboard `<RankedCandidateCard>` "Key Skills" header band.
   */
  skills_tags: string[];
  /**
   * ranking.system v3: stored 0–100 fit score emitted by the AI at rank
   * time. Null for legacy rows (backfill gives placeholders post-0067;
   * callers should fall back via fitScoreFromOverall).
   */
  fit_score: number | null;
}

interface RankingsListResponse {
  items: RankedCandidate[];
  next_cursor: string | null;
}

interface UseRankingOpts {
  jdId: string | undefined;
}

export function useRanking(
  opts: UseRankingOpts,
): UseQueryResult<RankedCandidate[] | null, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { jdId } = opts;

  return useQuery<RankedCandidate[] | null, ApiError>({
    queryKey: ["rankings", tenantSlug, jdId],
    queryFn: async () => {
      try {
        const r = await apiClient.get<RankingsListResponse>(
          `/api/t/${tenantSlug}/rankings`,
          { params: { jd_id: jdId } },
        );
        // Production returns 200 OK + {items: []} when the JD exists but has
        // no completed ranking_runs row yet (ai.py:208-221). Map that to
        // null so Dashboard's `rankings == null` pre-rank UX gate fires
        // (Dashboard.tsx:109) and renders cards in null-projection with
        // the hint banner. Live-URL bug fix — Batch 30h.1.5.
        return r.data.items.length === 0 ? null : r.data.items;
      } catch (err) {
        // 404: JD not visible to this tenant (cross-tenant or never-existed).
        // Surface as null data, not an error — empty state UI handles it.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: !!tenantSlug && !!jdId,
    staleTime: 30_000,
  });
}
